import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import Web3 from 'web3';
import './SellPage.css';
import Popup from './Popup';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5001';

function SellPage() {
  const [showPopup, setShowPopup] = useState(false);
  const [popupMessage, setPopupMessage] = useState('');
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    price: '',
    teamSize: '',
    askingPriceReasoning: '',
    lastMonthRevenue: '',
    lastMonthProfit: '',
    ttmRevenue: '',
    ttmProfit: '',
    websiteUrl: '',
    keyMetrics: ''
  });
  const [web3, setWeb3] = useState(null);
  const [account, setAccount] = useState('');
  const [isWeb3Ready, setIsWeb3Ready] = useState(false);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const initializeWeb3 = async () => {
    if (window.ethereum) {
      try {
        const web3Instance = new Web3(window.ethereum);
        await window.ethereum.request({ method: 'eth_requestAccounts' });
        setWeb3(web3Instance);
        const accounts = await web3Instance.eth.getAccounts();
        setAccount(accounts[0]);
        setIsWeb3Ready(true);
      } catch (error) {
        console.error('Error initializing Web3:', error);
        alert('Failed to connect to MetaMask. Please make sure it\'s installed and unlocked.');
      }
    } else {
      alert('Please install MetaMask or another Ethereum-compatible browser extension.');
    }
  };

  useEffect(() => {
    initializeWeb3();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!isWeb3Ready) {
      setPopupMessage('Please connect to MetaMask first.');
      setShowPopup(true);
      return;
    }

    const listingData = {
      ...formData,
      seller: account,
    };

    try {
      const response = await axios.post(`${API_URL}/api/listings`, listingData);
      console.log('Listing created successfully:', response.data);
      setPopupMessage('Listing created successfully!');
      setShowPopup(true);
      // Reset form fields
      setFormData({
        title: '',
        description: '',
        price: '',
        teamSize: '',
        askingPriceReasoning: '',
        lastMonthRevenue: '',
        lastMonthProfit: '',
        ttmRevenue: '',
        ttmProfit: '',
        websiteUrl: '',
        keyMetrics: ''
      });
    } catch (error) {
      console.error('Error creating listing:', error);
      setPopupMessage('Failed to create listing. Please try again.');
      setShowPopup(true);
    }
  };

  const handleClosePopup = () => {
    setShowPopup(false);
    navigate('/marketplace');
  };

  return (
    <div className="sell-container">
      <h1>Create a Listing</h1>
      <div className="telegram-banner">
        <h2>Important: Connect to Our Telegram Bot</h2>
        <p>To manage your listings and communicate with buyers, please connect to our Telegram bot:</p>
        <a href="https://t.me/codexchangebot" target="_blank" rel="noopener noreferrer" className="telegram-link">
          Connect to CodeXchange Bot
        </a>
      </div>
      {!isWeb3Ready && (
        <button onClick={initializeWeb3} className="connect-button">Connect to MetaMask</button>
      )}
      {isWeb3Ready && (
        <form onSubmit={handleSubmit} className="sell-form">
          <div className="form-group">
            <label htmlFor="title">Title</label>
            <input type="text" id="title" name="title" value={formData.title} onChange={handleChange} required placeholder="E.g., Profitable SaaS Business for Sale" />
          </div>
          <div className="form-group">
            <label htmlFor="description">Description</label>
            <textarea id="description" name="description" value={formData.description} onChange={handleChange} required placeholder="Provide a detailed description of your business, including its history, unique selling points, and growth potential." />
          </div>
          <div className="form-row">
            <div className="form-group">
              <label htmlFor="price">Asking Price (USDT)</label>
              <input type="number" id="price" name="price" value={formData.price} onChange={handleChange} required placeholder="E.g., 500000" />
            </div>
            <div className="form-group">
              <label htmlFor="teamSize">Team Size</label>
              <input type="number" id="teamSize" name="teamSize" value={formData.teamSize} onChange={handleChange} required placeholder="E.g., 5" />
            </div>
          </div>
          <div className="form-group">
            <label htmlFor="askingPriceReasoning">Asking Price Reasoning</label>
            <textarea id="askingPriceReasoning" name="askingPriceReasoning" value={formData.askingPriceReasoning} onChange={handleChange} required placeholder="Explain how you arrived at your asking price. Consider factors like revenue multiples, asset value, or industry standards." />
          </div>
          <div className="form-row">
            <div className="form-group">
              <label htmlFor="lastMonthRevenue">Last Month Revenue</label>
              <input type="number" id="lastMonthRevenue" name="lastMonthRevenue" value={formData.lastMonthRevenue} onChange={handleChange} required placeholder="E.g., 50000" />
            </div>
            <div className="form-group">
              <label htmlFor="lastMonthProfit">Last Month Profit</label>
              <input type="number" id="lastMonthProfit" name="lastMonthProfit" value={formData.lastMonthProfit} onChange={handleChange} required placeholder="E.g., 15000" />
            </div>
          </div>
          <div className="form-row">
            <div className="form-group">
              <label htmlFor="ttmRevenue">TTM Revenue</label>
              <input type="number" id="ttmRevenue" name="ttmRevenue" value={formData.ttmRevenue} onChange={handleChange} required placeholder="E.g., 600000" />
            </div>
            <div className="form-group">
              <label htmlFor="ttmProfit">TTM Profit</label>
              <input type="number" id="ttmProfit" name="ttmProfit" value={formData.ttmProfit} onChange={handleChange} required placeholder="E.g., 180000" />
            </div>
          </div>
          <div className="form-group">
            <label htmlFor="websiteUrl">Website URL</label>
            <input type="url" id="websiteUrl" name="websiteUrl" value={formData.websiteUrl} onChange={handleChange} required placeholder="https://www.example.com" />
          </div>
          <div className="form-group">
            <label htmlFor="keyMetrics">Key Metrics</label>
            <textarea id="keyMetrics" name="keyMetrics" value={formData.keyMetrics} onChange={handleChange} required placeholder="List important metrics such as MRR, churn rate, CAC, LTV, etc. One metric per line." />
          </div>
          <button type="submit" className="submit-button">Submit Listing</button>
        </form>
      )}
      {showPopup && <Popup message={popupMessage} onClose={handleClosePopup} />}
    </div>
  );
}

export default SellPage;